import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useApiQuery } from 'grabbitbot-legacy-ui'
import { projects } from '../../api/fee'
import { useProject } from '../../layout/projectContext'

export const ADMIN_USER_SECTIONS = [
  { label: 'Subscriptions', path: 'subscriptions' },
  { label: 'Products', path: 'products' },
  { label: 'Usage fees', path: 'usagefees' },
  { label: 'Transactions', path: 'transactions' },
  { label: 'Credits', path: 'credit' },
  { label: 'Payment info', path: 'paymentinfo' },
] as const

/**
 * Port of `AdminUserLocalMenuComponent`: shown on every per-user admin page.
 * Two menus — (1) switch USER (filterable list of `GET /projects/{id}/users`;
 * picking one always lands on that user's Payment info, as in legacy), and
 * (2) switch SECTION for the same user (keeps `?email=`).
 */
export function AdminUserLocalMenu({ email, title }: { email: string; title: string }) {
  const { projectId } = useProject()
  const location = useLocation()
  const users = useApiQuery(() => projects.users(projectId), [projectId])
  const [filter, setFilter] = useState('')
  const [open, setOpen] = useState<'users' | 'sections' | null>(null)
  const base = `/projects/${encodeURIComponent(projectId)}/admin/users`
  const q = `?email=${encodeURIComponent(email)}`

  const filtered = useMemo(
    () =>
      (users.data ?? []).filter((u) => !filter || u.username.toLowerCase().includes(filter.toLowerCase())),
    [users.data, filter],
  )

  return (
    <div className="portal-inline-actions fs-local-menu">
      <div className="fs-dropdown">
        <button type="button" aria-expanded={open === 'users'} onClick={() => setOpen(open === 'users' ? null : 'users')}>
          {email || 'Select user'} ▾
        </button>
        {open === 'users' && (
          <div className="fs-dropdown__panel" role="menu">
            <input
              type="search"
              aria-label="Filter users"
              placeholder="Search user…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <ul>
              {filtered.map((u) => (
                <li key={u.username}>
                  <Link
                    role="menuitem"
                    to={`${base}/paymentinfo?email=${encodeURIComponent(u.username)}`}
                    onClick={() => setOpen(null)}
                  >
                    {u.username}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="fs-dropdown">
        <button
          type="button"
          aria-expanded={open === 'sections'}
          onClick={() => setOpen(open === 'sections' ? null : 'sections')}
        >
          {title} ▾
        </button>
        {open === 'sections' && (
          <div className="fs-dropdown__panel" role="menu">
            <ul>
              {ADMIN_USER_SECTIONS.map((s) => {
                const to = `${base}/${s.path}`
                return (
                  <li key={s.path}>
                    <Link
                      role="menuitem"
                      to={`${to}${q}`}
                      aria-current={location.pathname === to ? 'page' : undefined}
                      onClick={() => setOpen(null)}
                    >
                      {s.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
